if (!customElements.get('ten-reasons-buy-gallery')) {
  customElements.define(
    'ten-reasons-buy-gallery',
    class TenReasonsBuyGallery extends HTMLElement {
      constructor() {
        super();
        this.bound = false;
        this.index = 0;
        this.pointerId = null;
        this.dragging = false;
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onViewportClick = this.onViewportClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
      }

      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.viewport = this.querySelector('[data-buy-viewport]');
        this.track = this.querySelector('[data-buy-track]');
        this.thumbsStrip = this.querySelector('[data-buy-thumbs]');
        this.live = this.querySelector('[data-buy-live]');
        this.slides = Array.from(this.querySelectorAll('[data-buy-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-buy-thumb]'));

        this.onThumbClick = (event) => {
          const thumb = event.target.closest('[data-buy-thumb]');
          if (!thumb || !this.contains(thumb)) return;
          const index = Number(thumb.dataset.buyIndex);
          if (!Number.isNaN(index)) this.goTo(index);
        };
        this.addEventListener('click', this.onThumbClick);

        if (this.viewport) {
          this.viewport.addEventListener('pointerdown', this.onPointerDown);
          this.viewport.addEventListener('lostpointercapture', this.onPointerUp);
          this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
          this.viewport.addEventListener('click', this.onViewportClick, true);
          this.viewport.addEventListener('keydown', this.onKeydown);
        }

        if (this.thumbs.length < 2 && this.dataset.designMode !== 'true' && this.thumbsStrip) {
          this.thumbsStrip.hidden = true;
        }

        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.goTo(0, false);
      }

      disconnectedCallback() {
        this.bound = false;
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.removeEventListener('click', this.onThumbClick);
        this.stopTracking();
        if (!this.viewport) return;
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport.removeEventListener('lostpointercapture', this.onPointerUp);
        this.viewport.removeEventListener('click', this.onViewportClick, true);
        this.viewport.removeEventListener('keydown', this.onKeydown);
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const thumb = event.target.closest('[data-buy-thumb]');
        if (!thumb) return;
        const index = Number(thumb.dataset.buyIndex);
        if (!Number.isNaN(index)) this.goTo(index, false);
      }

      onPointerDown(event) {
        if (!this.track || this.slides.length < 2) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (this.pointerId !== null) this.finishGesture();

        this.pointerId = event.pointerId;
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.deltaX = 0;
        this.dragging = false;
        this.gestureDecided = false;
        this.moved = false;
        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.onPointerUp);
        if (event.pointerType === 'mouse') event.preventDefault();
      }

      onPointerMove(event) {
        if (this.pointerId === null || event.pointerId !== this.pointerId || !this.track) return;
        const dx = event.clientX - this.startX;
        const dy = event.clientY - this.startY;

        if (!this.gestureDecided) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          this.gestureDecided = true;
          this.dragging = Math.abs(dx) > Math.abs(dy);
          if (!this.dragging) {
            this.finishGesture();
            return;
          }
          this.viewport.classList.add('is-dragging');
          this.track.style.transition = 'none';
        }

        if (!this.dragging) return;
        if (event.cancelable) event.preventDefault();
        this.moved = true;
        this.deltaX = dx;
        const atStart = this.index === 0 && dx > 0;
        const atEnd = this.index === this.slides.length - 1 && dx < 0;
        const resisted = atStart || atEnd ? dx * 0.35 : dx;
        this.track.style.transform = `translate3d(calc(${-this.index * 100}% + ${resisted}px), 0, 0)`;
      }

      onPointerUp(event) {
        if (this.pointerId === null) return;
        if (event.pointerId !== this.pointerId && event.type !== 'pointercancel' && event.type !== 'lostpointercapture') return;
        const width = this.viewport ? this.viewport.clientWidth : 1;
        const threshold = Math.min(48, width * 0.15);
        const shouldNext = this.dragging && this.deltaX < -threshold;
        const shouldPrev = this.dragging && this.deltaX > threshold;
        if (this.moved) this.suppressClick = true;
        this.finishGesture();

        if (shouldNext) this.goTo(this.index + 1);
        else if (shouldPrev) this.goTo(this.index - 1);
        else this.goTo(this.index);
      }

      onViewportClick(event) {
        if (!this.suppressClick) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      }

      finishGesture() {
        this.stopTracking();
        this.pointerId = null;
        this.dragging = false;
        this.deltaX = 0;
        if (this.viewport) this.viewport.classList.remove('is-dragging');
        if (this.track) this.track.style.transition = '';
      }

      stopTracking() {
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
      }

      onKeydown(event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
      }

      goTo(index, animate = true) {
        if (!this.track || this.slides.length === 0) return;
        const next = Math.max(0, Math.min(this.slides.length - 1, index));
        this.index = next;

        const reduce = this.prefersReducedMotion();
        if (!animate || reduce) this.track.style.transition = 'none';
        this.track.style.transform = `translate3d(${-next * 100}%, 0, 0)`;
        if (!animate || reduce) {
          this.track.offsetHeight;
          this.track.style.transition = '';
        }

        this.slides.forEach((slide, slideIndex) => {
          const active = slideIndex === next;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          if (active) slide.setAttribute('aria-current', 'true');
          else slide.removeAttribute('aria-current');
        });

        this.thumbs.forEach((thumb, thumbIndex) => {
          const active = thumbIndex === next;
          thumb.classList.toggle('is-active', active);
          if (active) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });

        const activeThumb = this.thumbs[next];
        if (this.thumbsStrip && activeThumb) {
          const left = activeThumb.offsetLeft - (this.thumbsStrip.clientWidth - activeThumb.clientWidth) / 2;
          this.thumbsStrip.scrollTo({
            left: Math.max(0, left),
            behavior: animate && !reduce ? 'smooth' : 'auto',
          });
        }

        if (this.live) {
          const label = this.slides[next].dataset.buyLabel;
          const position = next + 1;
          this.live.textContent = label
            ? `Image ${position} of ${this.slides.length}: ${label}`
            : `Image ${position} of ${this.slides.length}`;
        }
      }
    }
  );
}

if (!customElements.get('ten-reasons-buy-details')) {
  customElements.define(
    'ten-reasons-buy-details',
    class TenReasonsBuyDetails extends HTMLElement {
      constructor() {
        super();
        this.bound = false;
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
      }

      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        const weight = this.querySelector('[data-buy-weight].is-active') || this.querySelector('[data-buy-weight]');
        if (weight) this.selectWeight(weight);
        const offer = this.querySelector('[data-buy-offer].is-active') || this.querySelector('[data-buy-offer]');
        if (offer) this.selectOffer(offer);
      }

      disconnectedCallback() {
        this.bound = false;
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      onClick(event) {
        const weight = event.target.closest('[data-buy-weight]');
        if (weight && this.contains(weight)) {
          this.selectWeight(weight);
          return;
        }
        const offer = event.target.closest('[data-buy-offer]');
        if (offer && this.contains(offer)) this.selectOffer(offer);
      }

      onKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const offer = event.target.closest('[data-buy-offer]');
        if (!offer || !this.contains(offer) || event.target !== offer) return;
        event.preventDefault();
        this.selectOffer(offer);
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const weight = event.target.closest('[data-buy-weight]');
        if (weight) this.selectWeight(weight);
        const offer = event.target.closest('[data-buy-offer]');
        if (offer) this.selectOffer(offer);
      }

      selectWeight(weight) {
        this.querySelectorAll('[data-buy-weight]').forEach((button) => {
          const active = button === weight;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-checked', active ? 'true' : 'false');
        });
        const note = this.querySelector('[data-buy-weight-note]');
        if (note) note.textContent = weight.dataset.note || '';
      }

      selectOffer(offer) {
        this.querySelectorAll('[data-buy-offer]').forEach((card) => {
          const active = card === offer;
          card.classList.toggle('is-active', active);
          card.setAttribute('aria-checked', active ? 'true' : 'false');
        });
      }
    }
  );
}

if (!customElements.get('ten-reasons-buy-bundle')) {
  customElements.define(
    'ten-reasons-buy-bundle',
    class TenReasonsBuyBundle extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        const data = this.querySelector('[data-bundle-variants]');
        this.variants = [];
        try {
          this.variants = JSON.parse(data ? data.textContent : '[]');
        } catch (error) {
          this.variants = [];
        }
        this.root =
          this.closest('product-info') || this.closest('.ten-reasons-buy') || this;
        this.onSelection = () => {
          window.clearTimeout(this.syncTimer);
          this.syncTimer = window.setTimeout(() => this.sync(), 40);
        };
        this.root.addEventListener('click', this.onSelection);
        this.root.addEventListener('change', this.onSelection);
        const widget = this.root.querySelector('[data-zaib-widget]');
        if (widget && window.MutationObserver) {
          this.observer = new MutationObserver(this.onSelection);
          this.observer.observe(widget, { subtree: true, characterData: true, childList: true });
        }
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const sectionId = event?.data?.sectionId;
            if (!sectionId || sectionId === this.root.dataset?.section) {
              this.onSelection();
            }
          });
          this.optionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
            const target = event?.data?.event?.target;
            if (!target || this.root.contains(target)) {
              this.onSelection();
            }
          });
        }
        this.sync();
      }

      disconnectedCallback() {
        this.bound = false;
        if (!this.root) return;
        this.root.removeEventListener('click', this.onSelection);
        this.root.removeEventListener('change', this.onSelection);
        this.observer?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.optionChangeUnsubscriber?.();
      }

      sync() {
        const sizeButton =
          this.root.querySelector('.zaib_size-btn.zaib_active') ||
          this.root.querySelector('.zaib_size-btn[data-size="mini"]');
        const planCard = this.root.querySelector('[data-plan-type].zaib_active-plan');
        const size = sizeButton ? sizeButton.dataset.size : 'mini';
        const plan = planCard ? planCard.dataset.planType : 'subscription';
        const sellingPlanId = sizeButton ? sizeButton.dataset.sellingPlan : '';
        const matched = this.matchVariant(size);
        const variant = matched || this.currentVariant();
        if (matched) this.applyVariant(matched);

        const planPrice = this.readMoney('[data-zaib-sub-price]');
        const comparePrice = this.readMoney('[data-zaib-compare-price]');
        const oncePrice = this.readMoney('[data-zaib-onetime-price]');

        this.querySelectorAll('[data-bundle-item]').forEach((item) => {
          const visible = this.visibleFor(item.dataset.showFor, size, plan);
          item.hidden = !visible;
          if (!visible) return;

          const money = this.readItemMoney(item);
          if (money && item.dataset.priceMode !== 'custom') {
            const chosen = this.chooseMoney(money, item.dataset.priceMode, size, plan, sellingPlanId, variant?.id);
            if (item.dataset.priceMode === 'badge') {
              this.writeCompare(item, chosen.compareText);
            } else if (chosen.priceText) {
              this.writePrice(item, chosen.priceText, chosen.compareText);
            }
            return;
          }

          if (item.dataset.priceMode === 'custom' || item.dataset.priceMode === 'badge') return;

          let price = '';
          let compare = '';
          if (item.dataset.priceMode === 'variant' && variant) {
            price = variant.priceText || '';
            compare = variant.compareText || '';
          } else if (plan === 'subscription') {
            price = planPrice || variant?.priceText || '';
            compare = comparePrice || oncePrice || variant?.compareText || '';
          } else {
            price = oncePrice || variant?.priceText || '';
            compare = comparePrice && comparePrice !== price ? comparePrice : '';
          }
          this.writePrice(item, price, compare);
        });

        this.writeTotals(this.moneySample() || planPrice || oncePrice || comparePrice || '$0.00');
      }

      readItemMoney(item) {
        if (Object.prototype.hasOwnProperty.call(item, '_money')) return item._money;
        const node = item.querySelector('[data-item-money]');
        try {
          item._money = node ? JSON.parse(node.textContent) : null;
        } catch (error) {
          item._money = null;
        }
        return item._money;
      }

      chooseMoney(money, mode, size, plan, sellingPlanId, formVariantId) {
        const variants = money.variants || [];
        const variant =
          this.matchItemVariant(variants, size) ||
          variants.find((entry) => String(entry.id) === String(formVariantId)) ||
          variants[0];
        if (!variant) return { priceText: '', compareText: '' };

        if (mode === 'badge') {
          return { priceText: '', compareText: variant.priceText || '' };
        }

        if (mode === 'plan' && plan === 'subscription' && variant.plans && variant.plans.length) {
          const allocation =
            variant.plans.find((entry) => String(entry.id) === String(sellingPlanId)) || variant.plans[0];
          return {
            priceText: allocation.priceText || variant.priceText || '',
            compareText: allocation.compareText || '',
          };
        }

        return { priceText: variant.priceText || '', compareText: variant.compareText || '' };
      }

      matchItemVariant(variants, size) {
        const needle = (size || '').toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (!needle) return null;
        const pattern = new RegExp('\\b' + needle + '\\b');
        return (
          variants.find((variant) => {
            const haystack = [variant.title, variant.option1, variant.option2, variant.option3]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return pattern.test(haystack);
          }) || null
        );
      }

      moneySample() {
        const node = this.querySelector('[data-bundle-price], [data-bundle-compare]');
        return node ? node.textContent.trim() : '';
      }

      matchVariant(size) {
        const needle = (size || '').toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (!needle) return null;
        const pattern = new RegExp('\\b' + needle + '\\b');
        return (
          this.variants.find((variant) => {
            const haystack = [variant.title, variant.option1, variant.option2, variant.option3]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return pattern.test(haystack);
          }) || null
        );
      }

      currentVariant() {
        const input = this.root.querySelector('form[action*="/cart/add"] input[name="id"]');
        const id = input ? String(input.value) : '';
        return this.variants.find((variant) => String(variant.id) === id) || this.variants[0] || null;
      }

      applyVariant(variant) {
        if (!variant) return;
        const input = this.root.querySelector('form[action*="/cart/add"] input[name="id"]');
        if (!input || input.value === String(variant.id)) return;
        input.value = variant.id;
        input.disabled = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      visibleFor(rule, size, plan) {
        if (!rule || rule === 'all') return true;
        if (rule === 'subscription' || rule === 'onetime') return rule === plan;
        return rule === size;
      }

      readMoney(selector) {
        const node = this.root.querySelector(selector);
        return node ? node.textContent.trim() : '';
      }

      writePrice(item, price, compare) {
        const priceNode = item.querySelector('[data-bundle-price]');
        if (priceNode) priceNode.textContent = price;
        this.writeCompare(item, compare && compare !== price ? compare : '');
      }

      writeCompare(item, compare) {
        const compareNode = item.querySelector('[data-bundle-compare]');
        if (!compareNode) return;
        compareNode.textContent = compare || '';
        compareNode.hidden = !compareNode.textContent;
      }

      writeTotals(sample) {
        let value = 0;
        let pay = 0;
        let counted = 0;
        this.querySelectorAll('[data-bundle-item]').forEach((item) => {
          if (item.hidden || item.dataset.count !== 'true') return;
          const compare = this.amount(item.querySelector('[data-bundle-compare]')?.textContent || item.dataset.customCompare);
          const price = this.amount(item.querySelector('[data-bundle-price]')?.textContent || item.dataset.customPrice);
          const full = compare || price || 0;
          const due = item.dataset.priceMode === 'badge' ? 0 : price || 0;
          value += full;
          pay += due;
          counted += 1;
        });
        if (!counted) return;
        const total = this.querySelector('[data-bundle-total]');
        const today = this.querySelector('[data-bundle-today]');
        const save = this.querySelector('[data-bundle-save]');
        if (total) total.textContent = this.formatLike(sample, value);
        if (today) today.textContent = this.formatLike(sample, pay);
        if (save) save.textContent = this.formatLike(sample, Math.max(0, value - pay));
      }

      amount(text) {
        if (!text) return 0;
        const cleaned = text.replace(/[^0-9.,]/g, '');
        if (!cleaned) return 0;
        const lastComma = cleaned.lastIndexOf(',');
        const lastDot = cleaned.lastIndexOf('.');
        const normalized = lastComma > lastDot ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned.replace(/,/g, '');
        const value = Number(normalized);
        return Number.isFinite(value) ? value : 0;
      }

      formatLike(sample, amount) {
        const source = String(sample || '$0.00').trim();
        const match = source.match(/^(\D*)(\d[\d.,]*)(\D*)$/);
        if (!match) return source;
        const prefix = match[1];
        const number = match[2];
        const suffix = match[3];
        const lastComma = number.lastIndexOf(',');
        const lastDot = number.lastIndexOf('.');
        let decimalSep = '';
        let decimals = 0;
        if (lastComma > lastDot) {
          decimalSep = ',';
          decimals = number.length - lastComma - 1;
        } else if (lastDot > -1) {
          decimalSep = '.';
          decimals = number.length - lastDot - 1;
        }
        let formatted = amount.toFixed(decimals);
        if (decimalSep === ',') formatted = formatted.replace('.', ',');
        const groupSep =
          decimalSep === ',' && number.includes('.') && number.indexOf('.') < lastComma
            ? '.'
            : decimalSep === '.' && number.includes(',')
              ? ','
              : '';
        if (groupSep) {
          const parts = decimalSep ? formatted.split(decimalSep) : [formatted];
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
          formatted = parts.join(decimalSep);
        }
        return prefix + formatted + suffix;
      }
    }
  );
}

if (!customElements.get('ten-reasons-buy-faqs')) {
  customElements.define(
    'ten-reasons-buy-faqs',
    class TenReasonsBuyFaqs extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.duration = 350;
        this.addEventListener('click', (event) => {
          const summary = event.target.closest('summary');
          if (!summary || !this.contains(summary)) return;
          event.preventDefault();
          const details = summary.parentElement;
          if (!details || details.tagName !== 'DETAILS') return;
          if (details.open) {
            this.close(details);
            return;
          }
          this.querySelectorAll('details[open]').forEach((other) => {
            if (other !== details) this.close(other);
          });
          this.open(details);
        });
      }

      reduced() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      panel(details) {
        return details.querySelector('.accordion__content');
      }

      open(details) {
        const panel = this.panel(details);
        if (!panel) {
          details.open = true;
          return;
        }
        if (this.reduced()) {
          this.clear(panel);
          details.open = true;
          return;
        }
        details.open = true;
        panel.style.overflow = 'hidden';
        panel.style.transition = 'none';
        panel.style.height = '0px';
        const target = panel.scrollHeight;
        panel.offsetHeight;
        panel.style.transition = `height ${this.duration}ms ease`;
        panel.style.height = `${target}px`;
        this.after(panel, () => {
          if (!details.open) return;
          panel.style.height = 'auto';
          panel.style.overflow = '';
          panel.style.transition = '';
        });
      }

      close(details) {
        const panel = this.panel(details);
        if (!panel || !details.open) return;
        if (this.reduced()) {
          details.open = false;
          this.clear(panel);
          return;
        }
        panel.style.overflow = 'hidden';
        panel.style.transition = 'none';
        panel.style.height = `${panel.getBoundingClientRect().height}px`;
        panel.offsetHeight;
        panel.style.transition = `height ${this.duration}ms ease`;
        panel.style.height = '0px';
        this.after(panel, () => {
          details.open = false;
          this.clear(panel);
        });
      }

      clear(panel) {
        panel.style.height = '';
        panel.style.overflow = '';
        panel.style.transition = '';
      }

      after(panel, done) {
        const token = (panel.faqToken || 0) + 1;
        panel.faqToken = token;
        const finish = (event) => {
          if (event && event.target !== panel) return;
          if (panel.faqToken !== token) return;
          window.clearTimeout(timer);
          panel.removeEventListener('transitionend', finish);
          done();
        };
        const timer = window.setTimeout(finish, this.duration + 50);
        panel.addEventListener('transitionend', finish);
      }
    }
  );
}
